'use client'

import PreviewTrialCard from '@components/app/SearchLandingPage/TrialCard/Preview'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { classMerge } from '@components/utilities/classMerge'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import type { AlgoliaTrialHit } from '@modules/trials/search/const'
import type { Hit } from 'instantsearch.js'
import React from 'react'

interface TrialCardPreviewOverlayProps {
  isOverlayOpen: boolean
  previewTrial?: Hit<AlgoliaTrialHit>
  setIsOverlayOpen: React.Dispatch<React.SetStateAction<boolean>>
  setPreviewTrial: React.Dispatch<
    React.SetStateAction<Hit<AlgoliaTrialHit> | undefined>
  >
  trialsToPreview: Hit<AlgoliaTrialHit>[]
}

const TrialCardPreviewOverlay: React.FC<TrialCardPreviewOverlayProps> =
  React.memo(
    ({
      isOverlayOpen,
      previewTrial,
      setIsOverlayOpen,
      setPreviewTrial,
      trialsToPreview,
    }) => {
      // If the overlay is not open, don't render anything
      if (!isOverlayOpen || !previewTrial) {
        return null
      }

      // Pagination Helpers
      const handlePrevious = () => {
        if (!previewTrial) {
          return
        }
        const currentIndex = trialsToPreview.findIndex(
          (trial) => trial.nctId === previewTrial.nctId,
        )
        const prevIndex =
          currentIndex <= 0 ? trialsToPreview.length - 1 : currentIndex - 1
        setPreviewTrial(trialsToPreview[prevIndex])
      }
      const handleNext = () => {
        if (!previewTrial) {
          return
        }
        const currentIndex = trialsToPreview.findIndex(
          (trial) => trial.nctId === previewTrial.nctId,
        )
        const nextIndex =
          currentIndex >= trialsToPreview.length - 1 ? 0 : currentIndex + 1
        setPreviewTrial(trialsToPreview[nextIndex])
      }

      return (
        <div className='absolute bottom-8 z-mapOverlay w-full lg:left-[23rem] lg:top-6 lg:w-auto'>
          <div className='relative flex flex-col items-center'>
            {previewTrial && (
              <PreviewTrialCard
                onClose={() => {
                  setIsOverlayOpen(false)
                }}
                {...previewTrial}
              />
            )}
            {trialsToPreview?.length > 1 && (
              <div className='mt-2 flex items-center justify-center space-x-1.5'>
                <Button.Icon
                  className={classMerge(
                    buttonClassNames.legacy.outline.white,
                    'w-full rounded-lg border-2 border-neutral300 !px-2.5',
                  )}
                  icon={faArrowLeft}
                  iconClassName='text-primary600'
                  iconPosition='left'
                  iconSize='xs'
                  onClick={handlePrevious}
                />
                <Button.Icon
                  className={classMerge(
                    buttonClassNames.legacy.outline.white,
                    'w-full rounded-lg border-2 border-neutral300 !px-2.5',
                  )}
                  icon={faArrowRight}
                  iconClassName='text-primary600'
                  iconPosition='left'
                  iconSize='xs'
                  onClick={handleNext}
                />
              </div>
            )}
          </div>
        </div>
      )
    },
  )

TrialCardPreviewOverlay.displayName = 'TrialCardPreviewOverlay'
export default TrialCardPreviewOverlay
